// Matches the server: an employee must be employed after they were born, and
// be at least the country's minimum employment age on their employment date
import { APP_CONFIG } from "../../../helpers/appHelpers";
import { useGetCountry } from "../../../services/country.service";
import useCompanyStore from "../../../state-management/useCompanyStore";

/** Used when the company's country has no minimum employment age */
export const DEFAULT_MINIMUM_EMPLOYMENT_AGE = 16;

/** A YYYY-MM-DD (or ISO) value as UTC midnight, as the server stores it */
const toDay = (value?: string | null): Date | null => {
	if (!value) return null;
	const date = new Date(value.slice(0, 10));
	return Number.isNaN(date.getTime()) ? null : date;
};

/** Age in whole years on a date */
export const ageOn = (born: Date, on: Date): number => {
	let age = on.getUTCFullYear() - born.getUTCFullYear();
	const months = on.getUTCMonth() - born.getUTCMonth();
	if (months < 0 || (months === 0 && on.getUTCDate() < born.getUTCDate())) {
		age--;
	}
	return age;
};

/**
 * Checks the employment date against the date of birth. Passes while either
 * is missing, as they are on different steps of the form.
 */
export const validateEmploymentAge = (
	dateOfBirth: string | null | undefined,
	employmentDate: string | null | undefined,
	minimumAge?: number | null,
): true | string => {
	const born = toDay(dateOfBirth);
	const employed = toDay(employmentDate);
	if (!born || !employed) return true;
	if (employed <= born) {
		return "Employment date must be after the date of birth";
	}
	const limit = minimumAge ?? DEFAULT_MINIMUM_EMPLOYMENT_AGE;
	const age = ageOn(born, employed);
	if (age < limit) {
		return `Employee must be at least ${limit} years old on their employment date, but would have been ${age}`;
	}
	return true;
};

/**
 * Whether an edited date differs from the stored one. Edits only check
 * changed dates, as the server does, so employees saved with invalid dates
 * can still be edited.
 */
export const isDateChanged = (
	value: string | null | undefined,
	stored: string | null | undefined,
): boolean => toDay(value)?.getTime() !== toDay(stored)?.getTime();

/**
 * The check for an edit: only when either date changed, against the dates
 * the employee will end up with
 */
export const validateEditedEmploymentAge = (
	dates: { dateOfBirth?: string | null; employmentDate?: string | null },
	stored: { dateOfBirth?: string | null; employmentDate?: string | null },
	minimumAge?: number | null,
): true | string => {
	const changed =
		isDateChanged(dates.dateOfBirth, stored.dateOfBirth) ||
		isDateChanged(dates.employmentDate, stored.employmentDate);
	return changed
		? validateEmploymentAge(
				dates.dateOfBirth,
				dates.employmentDate,
				minimumAge,
			)
		: true;
};

/** The current company's country's minimum employment age, if it has one */
export const useMinimumEmploymentAge = (): number | null | undefined => {
	const { currentCompany } = useCompanyStore();
	const { data: countriesData } = useGetCountry({
		limit: APP_CONFIG.PAGE_LIMIT,
	});
	return countriesData?.data?.find(
		(country) => country.id === currentCompany?.countryId,
	)?.minimumEmploymentAge;
};
